import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion } from 'framer-motion';
const smoothEase = [0.22, 1, 0.36, 1] as const;
declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: (() => void) | undefined;
  }
}
export function HeroSection() {
  const playerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [videoReady, setVideoReady] = useState(false);
  const [introFinished, setIntroFinished] = useState(false);
  useEffect(() => {
    const loadAPI = () => {
      if (window.YT && window.YT.Player) {
        createPlayer();
        return;
      }
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
      window.onYouTubeIframeAPIReady = createPlayer;
    };
    const createPlayer = () => {
      if (!containerRef.current || playerRef.current) return;
      playerRef.current = new window.YT.Player(containerRef.current, {
        videoId: 'DKj9pCjrAg4',
        playerVars: {
          autoplay: 1,
          mute: 1,
          controls: 0,
          showinfo: 0,
          rel: 0,
          loop: 1,
          playlist: 'DKj9pCjrAg4',
          modestbranding: 1,
          playsinline: 1,
          disablekb: 1,
          iv_load_policy: 3,
          fs: 0,
          cc_load_policy: 0,
          origin: window.location.origin
        },
        events: {
          onReady: (event: any) => {
            event.target.mute();
            event.target.playVideo();
            setTimeout(() => setVideoReady(true), 600);
          },
          onStateChange: (event: any) => {
            if (event.data === window.YT.PlayerState.ENDED) {
              event.target.seekTo(0);
              event.target.playVideo();
            }
          }
        }
      });
    };
    loadAPI();
    return () => {
      if (playerRef.current?.destroy) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, []);
  return <section className="relative h-screen w-full overflow-hidden bg-black" data-id="element-79">
      {/* Fallback image */}
      <div className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-[1.5s] ease-out" style={{
      backgroundImage: 'url(https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&q=80)',
      opacity: videoReady ? 0 : 1
    }} data-id="element-80" />

      {/* YouTube Player */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden transition-opacity duration-[1.5s] ease-out" style={{
      opacity: videoReady ? 1 : 0
    }} data-id="element-81">
        <div ref={containerRef} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" style={{
        width: '177.78vh',
        height: '56.25vw',
        minWidth: '100%',
        minHeight: '100%'
      }} data-id="element-82" />
      </div>

      {/* Intro Sketch Video */}
      <video src="https://cdn.magicpatterns.com/uploads/gpHX2X4bWhYhg3fGEHgCmV/Nose_to_tail_Timelapse_of_a_detailed_pencil_sketch_of_a_Victorian_heritage_hou.mp4" autoPlay muted playsInline onEnded={() => setIntroFinished(true)} onError={() => setIntroFinished(true)} className="absolute inset-0 w-full h-full object-cover pointer-events-none transition-opacity duration-[2s] ease-in-out" style={{
      opacity: introFinished ? 0 : 1
    }} data-id="element-83" />

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 via-40% to-black/15" data-id="element-84" />

      {/* Content */}
      <div className="absolute inset-0 flex flex-col justify-end px-6 pb-28 md:px-12 md:pb-32 lg:px-16 max-w-7xl mx-auto w-full" data-id="element-85">
        <div className="max-w-3xl" data-id="element-86">
          <motion.div initial={{
          scaleX: 0
        }} animate={{
          scaleX: 1
        }} transition={{
          duration: 1.2,
          delay: 0.6,
          ease: smoothEase
        }} className="w-12 h-px bg-white/50 origin-left mb-6" data-id="element-87" />

          <motion.span initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.9,
          delay: 0.8,
          ease: smoothEase
        }} className="block text-white/70 text-[11px] md:text-xs font-medium tracking-[0.35em] uppercase mb-5" data-id="element-88">
            Expressions of Interest
          </motion.span>

          <motion.h1 initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 1,
          ease: smoothEase
        }} className="font-playfair text-[2.75rem] md:text-7xl lg:text-8xl text-white leading-[1.05] mb-3" data-id="element-89">
            10 Union Street
          </motion.h1>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 1.2,
          ease: smoothEase
        }} className="font-playfair text-2xl md:text-4xl lg:text-5xl text-white/50 italic" data-id="element-90">
            Armadale
          </motion.h2>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 2,
      duration: 1
    }} className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3" data-id="element-91">
        <span className="text-white/35 text-[10px] tracking-[0.3em] uppercase" data-id="element-92">
          Scroll
        </span>
        <div className="relative w-px h-10 bg-white/15 overflow-hidden" data-id="element-93">
          <motion.div animate={{
          y: ['-100%', '100%']
        }} transition={{
          repeat: Infinity,
          duration: 2,
          ease: 'easeInOut'
        }} className="absolute inset-x-0 h-1/2 bg-white/40" data-id="element-94" />
        </div>
      </motion.div>
    </section>;
}